import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiCpu, FiSave, FiLoader, FiZap, FiCheck, FiActivity, FiAlertCircle, FiPlay, FiInbox } from 'react-icons/fi';
import { supabase } from '@/integrations/supabase/client';

// ---------------------------------------------------------------------
// Test payload — a small ping we send to each provider to prove:
//   1. The API key is present + valid
//   2. The model actually responds
//   3. The response envelope is what downstream code expects
// Kept deliberately tiny so it costs ~pennies to run even against
// Opus 4.8.
// ---------------------------------------------------------------------
const TEST_PROMPT = 'Reply with just the word OK. Nothing else.';

// ---------------------------------------------------------------------
// V3 AI provider config.
//
// Two providers: OpenAI (GPT) and Anthropic (Claude). Gemini has been
// removed — replaced by Anthropic native Claude via a shared adapter
// (supabase/functions/_shared/anthropicAdapter.ts) so both providers
// route through identical OpenAI-shaped responses downstream.
//
// If a Claude call fails (missing key, quota, network), users keep
// operating on GPT — the fallback is set at the request layer, not
// here.
// ---------------------------------------------------------------------
const AI_PROVIDERS = [
  {
    id: 'openai',
    name: 'OpenAI · ChatGPT',
    tagline: 'GPT-5.5 — flagship analysis quality (2026)',
    accent: 'cyan',
    // Real OpenAI model IDs as of 2026. GPT-5.5 is the current
    // flagship; gpt-4o remains available for cost-sensitive tenants;
    // gpt-4o-mini for cheap batch work; o1 for reasoning-heavy asks.
    // Sandbox test earlier proved gpt-5.5 responds cleanly:
    //     ✓ OPENAI · GPT-5.5   OK
    models: ['gpt-5.5', 'gpt-5.4', 'gpt-4o', 'gpt-4o-mini', 'o1'],
    keyEnv: 'OPENAI_API_KEY',
    keyHint: 'Uses your OpenAI API key (set in Supabase secrets).',
  },
  {
    id: 'anthropic',
    name: 'Anthropic · Claude',
    tagline: 'Sonnet · Opus · Fable — full latest lineup',
    accent: 'purple',
    // Full premium lineup (2026). Order matters — top = default:
    //   sonnet-5  best cost/quality balance (recommended default)
    //   opus-4-8  flagship — deepest reasoning, best for hardest charts
    //   opus-4-7  fast Opus variant, cheaper per call
    //   fable-5   latest generation, experimental
    // Haiku is intentionally excluded — its vision quality on chart
    // reading doesn't clear the bar for a premium trading platform.
    models: [
      'claude-sonnet-5',
      'claude-opus-4-8',
      'claude-opus-4-7',
      'claude-fable-5',
    ],
    keyEnv: 'ANTHROPIC_API_KEY',
    keyHint: 'Uses your Anthropic API key (set in Supabase secrets).',
  },
] as const;

type Provider = typeof AI_PROVIDERS[number];

const DEFAULT_MODEL_FOR: Record<string, string> = {
  openai: 'gpt-5.5',
  anthropic: 'claude-sonnet-5',
};

interface TestResult {
  provider: 'openai' | 'anthropic';
  model: string;              // what we ASKED for
  actual_model?: string;      // what the provider ACTUALLY served (from response.model)
                              // — reveals silent aliasing (e.g. gpt-5.5 → gpt-4o-2024-...)
  ok: boolean;
  latency_ms: number;
  response_snippet?: string;
  error?: string;
}

// Fire one test ping against a specific provider. Uses the openai-chat
// Edge Function with a _force_provider override so we can hit either
// backend without changing the global admin_settings — critical: the
// switch is admin-only, GPT stays live for all real users during
// testing. If _force_user_id is set, the function looks up that user's
// per-profile ai_provider override to test their actual experience.
async function pingProvider(
  provider: 'openai' | 'anthropic',
  model: string,
  forceUserId?: string | null
): Promise<TestResult> {
  const startedAt = performance.now();
  try {
    const { data, error } = await supabase.functions.invoke('openai-chat', {
      body: {
        messages: [{ role: 'user', content: TEST_PROMPT }],
        model,
        max_tokens: 20,
        temperature: 0,
        _force_provider: provider,
        _force_user_id: forceUserId || undefined,
      },
    });
    const latency_ms = Math.round(performance.now() - startedAt);

    if (error) {
      // supabase-js FunctionsHttpError puts the raw Response object on
      // .context (a Response object, not a plain body). We need to
      // await response.text()/.json() to actually see what the Edge
      // Function returned — otherwise error.message is just the
      // useless generic "non-2xx status code" wrapper.
      let extra = '';
      let status = 0;
      try {
        const ctx: any = (error as any).context;
        if (ctx && typeof ctx.text === 'function') {
          status = ctx.status || 0;
          const bodyText = await ctx.text();
          if (bodyText) {
            try {
              const parsed = JSON.parse(bodyText);
              if (parsed?.error) extra = ` — ${parsed.error}`;
              else extra = ` — ${bodyText.slice(0, 200)}`;
            } catch {
              extra = ` — ${bodyText.slice(0, 200)}`;
            }
          }
        }
      } catch { /* swallow — we still report the outer error below */ }
      const statusStr = status ? ` [HTTP ${status}]` : '';
      return { provider, model, ok: false, latency_ms, error: `${error.message || String(error)}${statusStr}${extra}` };
    }
    if (data?.error) {
      return { provider, model, ok: false, latency_ms, error: String(data.error) };
    }
    const content = data?.choices?.[0]?.message?.content || '';
    if (!content) {
      return { provider, model, ok: false, latency_ms, error: 'Empty response' };
    }
    // Extract the model the API ACTUALLY served (from response.model).
    // Reveals silent aliasing: request 'gpt-5.5', get 'gpt-4o-2024-08-06'
    // back = OpenAI silently downgraded. Same for Anthropic.
    const actual_model = data?.model || undefined;
    return {
      provider,
      model,
      actual_model,
      ok: true,
      latency_ms,
      response_snippet: content.trim().slice(0, 80),
    };
  } catch (err: any) {
    return {
      provider,
      model,
      ok: false,
      latency_ms: Math.round(performance.now() - startedAt),
      error: err?.message || 'Unknown error',
    };
  }
}

const AISettingsPanel: React.FC = () => {
  const [provider, setProvider] = useState<string>('openai');
  const [model, setModel] = useState<string>('gpt-5.5');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [testing, setTesting] = useState<'current' | 'both' | 'user' | null>(null);
  const [testResults, setTestResults] = useState<TestResult[]>([]);
  const [testUserEmail, setTestUserEmail] = useState<string>('');
  const [testUserError, setTestUserError] = useState<string | null>(null);

  // Per-user override state — separate from the test panel above so the
  // admin can lock a specific user to a specific model without touching
  // the platform-wide default.
  const [overrideEmail, setOverrideEmail] = useState<string>('');
  const [overrideProvider, setOverrideProvider] = useState<string>('openai');
  const [overrideModel, setOverrideModel] = useState<string>('gpt-5.5');
  const [overrideStatus, setOverrideStatus] = useState<{
    kind: 'idle' | 'loading' | 'success' | 'error' | 'cleared';
    message?: string;
  }>({ kind: 'idle' });
  const [overrideCurrent, setOverrideCurrent] = useState<{
    provider: string | null;
    model: string | null;
  } | null>(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('admin_settings')
        .select('setting_key, setting_value')
        .in('setting_key', ['ai_provider', 'ai_model']);
      if (error) throw error;
      if (data) {
        let seenProvider = 'openai';
        let seenModel = 'gpt-5.5';
        data.forEach((row: any) => {
          if (row.setting_key === 'ai_provider' && row.setting_value) seenProvider = row.setting_value;
          if (row.setting_key === 'ai_model' && row.setting_value) seenModel = row.setting_value;
        });
        // Legacy migration — if a stale 'gemini' provider is still in
        // admin_settings from before this refactor, silently coerce to
        // OpenAI so the panel doesn't try to render a missing tile.
        if (seenProvider === 'gemini') {
          seenProvider = 'openai';
          seenModel = 'gpt-5.5';
        }
        // Legacy coerce: the pre-2026 codebase used 'gpt-5.4-mini'
        // as a mini SKU that OpenAI later folded into gpt-4o-mini
        // pricing tier. Coerce any lingering row to gpt-4o-mini so
        // the picker doesn't render an option that 404s.
        if (seenModel === 'gpt-5.4-mini') {
          seenModel = 'gpt-4o-mini';
        }
        setProvider(seenProvider);
        setModel(seenModel);
      }
    } catch (err) {
      console.error('Error fetching AI settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleProviderChange = (newProvider: string) => {
    setProvider(newProvider);
    setModel(DEFAULT_MODEL_FOR[newProvider] || '');
  };

  const saveSettings = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const entries = [
        { setting_key: 'ai_provider', setting_value: provider, description: 'AI provider: openai or anthropic' },
        { setting_key: 'ai_model', setting_value: model, description: 'AI model identifier' },
      ];
      for (const entry of entries) {
        const { error } = await supabase
          .from('admin_settings')
          .upsert(entry, { onConflict: 'setting_key' });
        if (error) throw error;
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('Error saving AI settings:', err);
    } finally {
      setSaving(false);
    }
  };

  // ------------------------------------------------------------------
  // Test handlers — never mutate admin_settings. Each test hits the AI
  // via _force_provider so the global config for real users is
  // untouched (GPT stays live for them the whole time).
  // ------------------------------------------------------------------
  const runCurrentTest = async () => {
    setTesting('current');
    setTestResults([]);
    const result = await pingProvider(provider as 'openai' | 'anthropic', model);
    setTestResults([result]);
    setTesting(null);
  };

  const runBothTest = async () => {
    setTesting('both');
    setTestResults([]);
    const [gpt, claude] = await Promise.all([
      pingProvider('openai', 'gpt-5.5'),
      pingProvider('anthropic', 'claude-sonnet-5'),
    ]);
    setTestResults([gpt, claude]);
    setTesting(null);
  };

  const runUserTest = async () => {
    const email = testUserEmail.trim().toLowerCase();
    if (!email) {
      setTestUserError('Enter a user email to test');
      return;
    }
    setTestUserError(null);
    setTesting('user');
    setTestResults([]);
    try {
      const { data: userRow, error: lookupError } = await supabase
        .from('user_profiles')
        .select('user_id, email, ai_provider, ai_model')
        .eq('email', email)
        .maybeSingle();

      if (lookupError) throw lookupError;
      if (!userRow) {
        setTestUserError(`No user found for ${email}`);
        setTesting(null);
        return;
      }

      // Respect per-user override if set, else fall back to the currently
      // saved global provider/model so we test what THIS user actually gets.
      const userProvider = (userRow.ai_provider || provider) as 'openai' | 'anthropic';
      const userModel = userRow.ai_model || model;
      const result = await pingProvider(userProvider, userModel, userRow.user_id);
      setTestResults([result]);
    } catch (err: any) {
      setTestUserError(err?.message || 'User lookup failed');
    } finally {
      setTesting(null);
    }
  };

  // ------------------------------------------------------------------
  // Per-user override handlers.
  // ------------------------------------------------------------------
  const loadUserOverride = async () => {
    const email = overrideEmail.trim().toLowerCase();
    if (!email) {
      setOverrideStatus({ kind: 'error', message: 'Enter a user email first' });
      return;
    }
    setOverrideStatus({ kind: 'loading' });
    setOverrideCurrent(null);
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('ai_provider, ai_model')
        .eq('email', email)
        .maybeSingle();
      if (error) throw error;
      if (!data) {
        setOverrideStatus({ kind: 'error', message: `No user found for ${email}` });
        return;
      }
      setOverrideCurrent({ provider: data.ai_provider, model: data.ai_model });
      if (data.ai_provider) setOverrideProvider(data.ai_provider);
      if (data.ai_model) setOverrideModel(data.ai_model);
      setOverrideStatus({ kind: 'idle' });
    } catch (err: any) {
      setOverrideStatus({ kind: 'error', message: err?.message || 'Lookup failed' });
    }
  };

  const saveUserOverride = async () => {
    const email = overrideEmail.trim().toLowerCase();
    if (!email) {
      setOverrideStatus({ kind: 'error', message: 'Enter a user email first' });
      return;
    }
    setOverrideStatus({ kind: 'loading' });
    try {
      const { data: existing, error: lookupError } = await supabase
        .from('user_profiles')
        .select('user_id')
        .eq('email', email)
        .maybeSingle();
      if (lookupError) throw lookupError;
      if (!existing) {
        setOverrideStatus({ kind: 'error', message: `No user found for ${email}` });
        return;
      }
      const { error } = await supabase
        .from('user_profiles')
        .update({ ai_provider: overrideProvider, ai_model: overrideModel })
        .eq('user_id', existing.user_id);
      if (error) throw error;
      setOverrideCurrent({ provider: overrideProvider, model: overrideModel });
      setOverrideStatus({
        kind: 'success',
        message: `${email} is now locked to ${overrideProvider} · ${overrideModel}`,
      });
    } catch (err: any) {
      setOverrideStatus({ kind: 'error', message: err?.message || 'Save failed' });
    }
  };

  const clearUserOverride = async () => {
    const email = overrideEmail.trim().toLowerCase();
    if (!email) {
      setOverrideStatus({ kind: 'error', message: 'Enter a user email first' });
      return;
    }
    setOverrideStatus({ kind: 'loading' });
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({ ai_provider: null, ai_model: null })
        .eq('email', email);
      if (error) throw error;
      setOverrideCurrent({ provider: null, model: null });
      setOverrideStatus({
        kind: 'cleared',
        message: `${email} now falls back to the global default`,
      });
    } catch (err: any) {
      setOverrideStatus({ kind: 'error', message: err?.message || 'Clear failed' });
    }
  };

  const overrideProviderData = AI_PROVIDERS.find(p => p.id === overrideProvider);

  const selectedProvider: Provider | undefined = AI_PROVIDERS.find(p => p.id === provider);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <FiLoader className="animate-spin text-cyan-400 text-2xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header — V3 terminal treatment */}
      <div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-cyan-400 mb-2">
          ▸ AI · Model configuration
        </div>
        <h2 className="text-2xl font-bold text-white flex items-center gap-2">
          <FiCpu className="text-cyan-400" />
          AI Model Configuration
        </h2>
        <p className="text-sm text-slate-400 mt-1">
          Choose which AI powers all market analyses on the platform. Users
          can override this individually via <code className="font-mono text-cyan-300/80">user_profiles.ai_provider</code>.
        </p>
      </div>

      {/* Provider tiles — V3 palette:
          - default: bg-slate-900/40, border-slate-800, subtle
          - active: solid border + colored glow, matching brand accent */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {AI_PROVIDERS.map((p) => {
          const isActive = provider === p.id;
          const accent = p.accent === 'cyan'
            ? {
                border: isActive ? 'border-cyan-400/70' : 'border-slate-800 hover:border-cyan-500/40',
                glow: isActive ? 'shadow-[0_0_28px_rgba(34,211,238,0.15)]' : '',
                bg: isActive ? 'bg-cyan-500/[0.06]' : 'bg-slate-900/50',
                dot: 'bg-cyan-400 shadow-[0_0_10px_rgba(34,211,238,0.7)]',
                text: 'text-cyan-300',
                label: '▸ GPT / CHATGPT',
              }
            : {
                border: isActive ? 'border-purple-400/70' : 'border-slate-800 hover:border-purple-500/40',
                glow: isActive ? 'shadow-[0_0_28px_rgba(168,85,247,0.18)]' : '',
                bg: isActive ? 'bg-purple-500/[0.07]' : 'bg-slate-900/50',
                dot: 'bg-purple-400 shadow-[0_0_10px_rgba(168,85,247,0.7)]',
                text: 'text-purple-300',
                label: '▸ CLAUDE / ANTHROPIC',
              };

          return (
            <motion.button
              key={p.id}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.99 }}
              onClick={() => handleProviderChange(p.id)}
              className={`relative p-5 rounded-xl border transition-all text-left ${accent.border} ${accent.bg} ${accent.glow}`}
            >
              {/* Active LED */}
              <div className="absolute top-4 right-4 flex items-center gap-2">
                {isActive ? (
                  <>
                    <div className={`w-1.5 h-1.5 rounded-full ${accent.dot} animate-pulse`} />
                    <span className={`font-mono text-[10px] uppercase tracking-widest ${accent.text}`}>Active</span>
                  </>
                ) : (
                  <div className="w-1.5 h-1.5 rounded-full bg-slate-700" />
                )}
              </div>

              <div className={`font-mono text-[10px] uppercase tracking-widest mb-3 ${isActive ? accent.text : 'text-slate-500'}`}>
                {accent.label}
              </div>
              <h3 className="text-lg font-semibold text-white">{p.name}</h3>
              <p className="text-sm text-slate-400 mt-1">{p.tagline}</p>

              <div className="mt-4 pt-3 border-t border-slate-800/70 flex items-center gap-2 text-[11px] text-slate-500 font-mono">
                <span className="text-slate-600">KEY:</span>
                <span className="text-slate-400">{p.keyEnv}</span>
              </div>
            </motion.button>
          );
        })}
      </div>

      {/* Model select + status readout */}
      {selectedProvider && (
        <motion.div
          key={provider}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-slate-900/50 border border-slate-800 rounded-xl p-5"
        >
          <div className="font-mono text-[10px] uppercase tracking-widest text-cyan-400 mb-3 flex items-center gap-2">
            <FiZap className="text-cyan-400" />
            ▸ Selected model
          </div>
          <select
            value={model}
            onChange={(e) => setModel(e.target.value)}
            className="w-full px-4 py-3 bg-slate-950 border border-slate-800 rounded-lg text-white font-mono text-sm focus:outline-none focus:border-cyan-500/60 focus:ring-1 focus:ring-cyan-500/30 transition-colors"
          >
            {selectedProvider.models.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>

          <div className={`mt-4 px-4 py-3 rounded-lg border font-mono text-[11px] leading-relaxed ${
            provider === 'openai'
              ? 'bg-cyan-500/[0.05] border-cyan-500/20 text-cyan-200/90'
              : 'bg-purple-500/[0.05] border-purple-500/20 text-purple-200/90'
          }`}>
            <div className="uppercase tracking-widest opacity-60 mb-1">
              {provider === 'openai' ? '▸ OpenAI · GPT' : '▸ Anthropic · Claude'}
            </div>
            {provider === 'openai'
              ? 'Analyses route through the OpenAI Chat/Vision API using your OPENAI_API_KEY.'
              : 'Analyses route through the native Anthropic Messages API using your ANTHROPIC_API_KEY. Same request/response contract as OpenAI — no downstream code change.'}
            <div className="mt-2 opacity-60">{selectedProvider.keyHint}</div>
          </div>
        </motion.div>
      )}

      {/* Save button — V3 accent gradient */}
      <div className="flex items-center gap-4">
        <button
          onClick={saveSettings}
          disabled={saving}
          className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-cyan-500 to-purple-500 text-white font-semibold rounded-lg shadow-[0_0_18px_rgba(34,211,238,0.25)] hover:shadow-[0_0_28px_rgba(168,85,247,0.35)] transition-shadow disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiSave className="w-4 h-4" />}
          {saving ? 'Saving…' : 'Save AI settings'}
        </button>
        {saved && (
          <motion.span
            initial={{ opacity: 0, x: -6 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-1.5 text-green-400 text-sm font-mono"
          >
            <FiCheck className="w-4 h-4" />
            SAVED
          </motion.span>
        )}
      </div>

      {/* ────────────────────────────────────────────────────────────
          TEST & VERIFY — admin-only sandbox for pinging the AI stack.
          Never mutates admin_settings. Real users stay on whatever
          provider is currently saved (GPT by default) while the admin
          tests any provider / any specific user email.
          ──────────────────────────────────────────────────────────── */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5 space-y-5">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-cyan-400 mb-2 flex items-center gap-2">
            <FiActivity className="text-cyan-400" />
            ▸ Test &amp; verify
          </div>
          <h3 className="text-lg font-semibold text-white">Sandbox tests</h3>
          <p className="text-sm text-slate-400 mt-1">
            Ping the AI stack without changing anything. Real users keep
            using the currently saved provider —{' '}
            <span className="font-mono text-cyan-300/80">{provider}</span> —
            while you test.
          </p>
        </div>

        {/* Row 1 — current config + both providers */}
        <div className="flex flex-wrap gap-3">
          <button
            onClick={runCurrentTest}
            disabled={testing !== null}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 hover:border-cyan-400/60 text-cyan-200 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {testing === 'current' ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiPlay className="w-4 h-4" />}
            Ping current config
          </button>
          <button
            onClick={runBothTest}
            disabled={testing !== null}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-purple-500/10 hover:bg-purple-500/20 border border-purple-500/30 hover:border-purple-400/60 text-purple-200 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {testing === 'both' ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiZap className="w-4 h-4" />}
            Test both providers (GPT + Claude)
          </button>
        </div>

        {/* Row 2 — specific user email */}
        <div className="pt-4 border-t border-slate-800/70 space-y-3">
          <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
            ▸ Test as specific user
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="email"
              value={testUserEmail}
              onChange={(e) => {
                setTestUserEmail(e.target.value);
                setTestUserError(null);
              }}
              placeholder="user@example.com"
              className="flex-1 px-4 py-2 bg-slate-950 border border-slate-800 rounded-lg text-white font-mono text-sm focus:outline-none focus:border-cyan-500/60 focus:ring-1 focus:ring-cyan-500/30 transition-colors placeholder:text-slate-600"
            />
            <button
              onClick={runUserTest}
              disabled={testing !== null || !testUserEmail.trim()}
              className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-cyan-500 to-purple-500 text-white text-sm font-semibold transition-shadow shadow-[0_0_14px_rgba(34,211,238,0.2)] hover:shadow-[0_0_22px_rgba(168,85,247,0.35)] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {testing === 'user' ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiPlay className="w-4 h-4" />}
              Run as user
            </button>
          </div>
          {testUserError && (
            <div className="flex items-center gap-2 text-sm text-red-300">
              <FiAlertCircle className="w-4 h-4" />
              {testUserError}
            </div>
          )}
          <p className="text-xs text-slate-500">
            Uses that user's per-profile <span className="font-mono text-slate-400">ai_provider</span> override
            if they have one, otherwise the current global setting. No changes are made — just a ping.
          </p>
        </div>

        {/* Test results grid */}
        {testResults.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className="pt-4 border-t border-slate-800/70 space-y-2"
          >
            <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">
              ▸ Results
            </div>
            {testResults.map((r, idx) => {
              const accent = r.provider === 'openai' ? 'cyan' : 'purple';
              return (
                <div
                  key={idx}
                  className={`rounded-lg border p-3 font-mono text-[11px] leading-relaxed ${
                    r.ok
                      ? accent === 'cyan'
                        ? 'bg-cyan-500/[0.05] border-cyan-500/30 text-cyan-100/90'
                        : 'bg-purple-500/[0.05] border-purple-500/30 text-purple-100/90'
                      : 'bg-red-500/[0.06] border-red-500/30 text-red-200/90'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="uppercase tracking-widest opacity-70">
                      {r.ok ? '✓' : '✗'} {r.provider} · {r.model}
                    </span>
                    <span className="opacity-60">{r.latency_ms} ms</span>
                  </div>
                  {/* Reveal silent aliasing — if the provider returned a
                      different model than what we requested, show a
                      warning line so the admin doesn't think they're
                      running Model X while they're actually on Model Y. */}
                  {r.ok && r.actual_model && r.actual_model !== r.model && (
                    <div className="opacity-70 mb-1 text-yellow-300">
                      ⚠ Aliased → actually served: <span className="uppercase">{r.actual_model}</span>
                    </div>
                  )}
                  {r.ok ? (
                    <div className="opacity-80">{r.response_snippet}</div>
                  ) : (
                    <div className="opacity-80">{r.error}</div>
                  )}
                </div>
              );
            })}
          </motion.div>
        )}
      </div>

      {/* ────────────────────────────────────────────────────────────
          PER-USER OVERRIDE — writes user_profiles.ai_provider +
          ai_model for a single user, letting the admin pin them to a
          specific model regardless of the global setting. Useful for:
            • VIP users on Opus 4.8
            • Testing Claude on your own account while keeping the rest
              of the platform on GPT
            • Locking a heavy user off an expensive model
          Clear override = fall back to the global default.
          ──────────────────────────────────────────────────────────── */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5 space-y-5">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-purple-400 mb-2 flex items-center gap-2">
            <FiZap className="text-purple-400" />
            ▸ Per-user override
          </div>
          <h3 className="text-lg font-semibold text-white">Force a user onto a specific model</h3>
          <p className="text-sm text-slate-400 mt-1">
            Writes to <span className="font-mono text-purple-300/80">user_profiles.ai_provider</span> +
            <span className="font-mono text-purple-300/80"> ai_model</span> for one user.
            Their next analysis uses this instead of the global setting.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="email"
            value={overrideEmail}
            onChange={(e) => {
              setOverrideEmail(e.target.value);
              setOverrideStatus({ kind: 'idle' });
              setOverrideCurrent(null);
            }}
            placeholder="user@example.com"
            className="flex-1 px-4 py-2 bg-slate-950 border border-slate-800 rounded-lg text-white font-mono text-sm focus:outline-none focus:border-purple-500/60 focus:ring-1 focus:ring-purple-500/30 transition-colors placeholder:text-slate-600"
          />
          <button
            onClick={loadUserOverride}
            disabled={overrideStatus.kind === 'loading' || !overrideEmail.trim()}
            className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-slate-800/70 hover:bg-slate-800 border border-slate-700 hover:border-slate-600 text-slate-200 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {overrideStatus.kind === 'loading' ? <FiLoader className="w-4 h-4 animate-spin" /> : null}
            Load current
          </button>
        </div>

        {overrideCurrent && (
          <div className="rounded-lg border border-slate-800 bg-slate-950/50 px-4 py-3 font-mono text-[11px] text-slate-400">
            <div className="uppercase tracking-widest text-slate-500 mb-1">▸ Current on file</div>
            {overrideCurrent.provider ? (
              <>
                <div>provider: <span className="text-purple-300">{overrideCurrent.provider}</span></div>
                <div>model:    <span className="text-purple-300">{overrideCurrent.model || '(none)'}</span></div>
              </>
            ) : (
              <div className="text-slate-500 italic">No override — user gets the global default.</div>
            )}
          </div>
        )}

        {/* Provider + model pickers for the override */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-1 block">Provider</label>
            <select
              value={overrideProvider}
              onChange={(e) => {
                setOverrideProvider(e.target.value);
                const p = AI_PROVIDERS.find(x => x.id === e.target.value);
                if (p) setOverrideModel(p.models[0]);
              }}
              className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-lg text-white font-mono text-sm focus:outline-none focus:border-purple-500/60"
            >
              {AI_PROVIDERS.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-1 block">Model</label>
            <select
              value={overrideModel}
              onChange={(e) => setOverrideModel(e.target.value)}
              className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-lg text-white font-mono text-sm focus:outline-none focus:border-purple-500/60"
            >
              {(overrideProviderData?.models || []).map(m => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Action row */}
        <div className="flex flex-wrap gap-3">
          <button
            onClick={saveUserOverride}
            disabled={overrideStatus.kind === 'loading' || !overrideEmail.trim()}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-cyan-500 to-purple-500 text-white text-sm font-semibold transition-shadow shadow-[0_0_14px_rgba(168,85,247,0.25)] hover:shadow-[0_0_22px_rgba(168,85,247,0.4)] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {overrideStatus.kind === 'loading' ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiSave className="w-4 h-4" />}
            Apply override
          </button>
          <button
            onClick={clearUserOverride}
            disabled={overrideStatus.kind === 'loading' || !overrideEmail.trim()}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 hover:border-red-400/60 text-red-200 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Clear override
          </button>
        </div>

        {overrideStatus.kind === 'success' && (
          <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-2 text-sm text-green-300"
          >
            <FiCheck className="w-4 h-4" />
            {overrideStatus.message}
          </motion.div>
        )}
        {overrideStatus.kind === 'cleared' && (
          <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-2 text-sm text-slate-300"
          >
            <FiCheck className="w-4 h-4" />
            {overrideStatus.message}
          </motion.div>
        )}
        {overrideStatus.kind === 'error' && (
          <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-2 text-sm text-red-300"
          >
            <FiAlertCircle className="w-4 h-4" />
            {overrideStatus.message}
          </motion.div>
        )}
      </div>

      {/* ────────────────────────────────────────────────────────────
          CLAUDE ACCESS REQUEST QUEUE — admin queue for users who
          submitted request_claude_access() from their account page.
          ──────────────────────────────────────────────────────────── */}
      <ClaudeAccessRequestsQueue />

      {/* ────────────────────────────────────────────────────────────
          PLAN → AI ROUTING — one row per subscription plan. Admin
          picks the primary model, an optional fallback, and a monthly
          $ cap per user. Backend auto-switches primary → fallback
          when a user's usage exceeds the cap.
          ──────────────────────────────────────────────────────────── */}
      <PlanMappingSection />

      {/* ────────────────────────────────────────────────────────────
          COST DASHBOARD — rolling 30d spend, calls, tokens, top
          spenders, and per-plan profit margin (plan revenue vs AI cost).
          ──────────────────────────────────────────────────────────── */}
      <CostDashboardSection />
    </div>
  );
};

// =====================================================================
// Claude Access Requests Queue
// =====================================================================
const ClaudeAccessRequestsQueue: React.FC = () => {
  const [rows, setRows] = useState<Array<{
    user_id: string;
    email: string;
    requested_at: string;
    note: string | null;
  }>>([]);
  const [loading, setLoading] = useState(true);
  const [deciding, setDeciding] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await supabase
        .from('user_profiles')
        .select('user_id, email, ai_access_requested_at, ai_access_requested_note')
        .not('ai_access_requested_at', 'is', null)
        .is('ai_access_decision', null)
        .order('ai_access_requested_at', { ascending: false });
      setRows((data || []).map((r: any) => ({
        user_id: r.user_id,
        email: r.email,
        requested_at: r.ai_access_requested_at,
        note: r.ai_access_requested_note,
      })));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const decide = async (userId: string, decision: 'granted' | 'denied') => {
    setDeciding(userId);
    try {
      await supabase.rpc('admin_decide_claude_access', {
        p_user_id: userId,
        p_decision: decision,
        p_model: 'claude-sonnet-5',
      });
      await load();
    } finally {
      setDeciding(null);
    }
  };

  return (
    <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5 space-y-4">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-purple-400 mb-2 flex items-center gap-2">
          <FiInbox className="text-purple-400" />
          ▸ Claude access requests
        </div>
        <h3 className="text-lg font-semibold text-white">Pending user requests</h3>
        <p className="text-sm text-slate-400 mt-1">
          Users who submitted <span className="font-mono text-purple-300/80">request_claude_access()</span>.
          Approve to write anthropic override on their profile, deny to record the decision.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 text-sm"><FiLoader className="animate-spin" /> Loading…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-slate-800 bg-slate-950/50 px-4 py-3 text-sm text-slate-500">
          No pending requests.
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map(r => (
            <div key={r.user_id} className="rounded-lg border border-purple-500/20 bg-purple-500/[0.04] p-3">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="font-mono text-sm text-white truncate">{r.email}</div>
                  <div className="font-mono text-[11px] text-slate-500">
                    requested {new Date(r.requested_at).toLocaleString()}
                  </div>
                  {r.note && (
                    <div className="text-xs text-slate-400 mt-1 italic">"{r.note}"</div>
                  )}
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    disabled={deciding === r.user_id}
                    onClick={() => decide(r.user_id, 'granted')}
                    className="px-3 py-1.5 rounded-md bg-cyan-500/15 hover:bg-cyan-500/25 border border-cyan-500/40 text-cyan-200 text-xs font-medium transition-colors disabled:opacity-50"
                  >
                    ✓ Approve → Sonnet 5
                  </button>
                  <button
                    disabled={deciding === r.user_id}
                    onClick={() => decide(r.user_id, 'denied')}
                    className="px-3 py-1.5 rounded-md bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-200 text-xs font-medium transition-colors disabled:opacity-50"
                  >
                    ✗ Deny
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// =====================================================================
// Plan Mapping Section — configure primary/fallback/cap per plan.
// =====================================================================
const PLAN_IDS = ['starter', 'pro_trader', 'institutional_ai', 'enterprise'];

const PlanMappingSection: React.FC = () => {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await supabase.from('ai_plan_mapping').select('*');
      const byPlan = new Map((data || []).map((r: any) => [r.plan_id, r]));
      setRows(PLAN_IDS.map(pid => byPlan.get(pid) || {
        plan_id: pid,
        provider: 'openai',
        model: 'gpt-5.5',
        fallback_provider: null,
        fallback_model: null,
        monthly_cap_usd: null,
      }));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const savePlan = async (row: any) => {
    setSaving(row.plan_id);
    try {
      await supabase.from('ai_plan_mapping').upsert({
        plan_id: row.plan_id,
        provider: row.provider,
        model: row.model,
        fallback_provider: row.fallback_provider,
        fallback_model: row.fallback_model,
        monthly_cap_usd: row.monthly_cap_usd,
      }, { onConflict: 'plan_id' });
      await load();
    } finally {
      setSaving(null);
    }
  };

  const clearPlan = async (planId: string) => {
    setSaving(planId);
    try {
      await supabase.from('ai_plan_mapping').delete().eq('plan_id', planId);
      await load();
    } finally {
      setSaving(null);
    }
  };

  const updateLocal = (planId: string, patch: any) => {
    setRows(prev => prev.map(r => r.plan_id === planId ? { ...r, ...patch } : r));
  };

  return (
    <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5 space-y-4">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-cyan-400 mb-2 flex items-center gap-2">
          <FiZap className="text-cyan-400" />
          ▸ Plan → AI routing
        </div>
        <h3 className="text-lg font-semibold text-white">Per-plan model + cap</h3>
        <p className="text-sm text-slate-400 mt-1">
          Every user on this plan gets the <b>primary</b> model — until their monthly cost hits the cap, then
          the backend auto-switches to <b>fallback</b> for the rest of the month.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 text-sm"><FiLoader className="animate-spin" /> Loading…</div>
      ) : (
        <div className="space-y-3">
          {rows.map(row => (
            <div key={row.plan_id} className="rounded-lg border border-slate-800 bg-slate-950/40 p-3 space-y-2">
              <div className="font-mono text-xs uppercase tracking-widest text-cyan-300">▸ {row.plan_id}</div>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-2 items-end">
                <div>
                  <label className="text-[10px] font-mono uppercase text-slate-500 block mb-1">Primary provider</label>
                  <select
                    value={row.provider}
                    onChange={(e) => updateLocal(row.plan_id, { provider: e.target.value, model: DEFAULT_MODEL_FOR[e.target.value] })}
                    className="w-full px-2 py-1.5 bg-slate-950 border border-slate-800 rounded text-white font-mono text-xs"
                  >
                    {AI_PROVIDERS.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-mono uppercase text-slate-500 block mb-1">Primary model</label>
                  <select
                    value={row.model}
                    onChange={(e) => updateLocal(row.plan_id, { model: e.target.value })}
                    className="w-full px-2 py-1.5 bg-slate-950 border border-slate-800 rounded text-white font-mono text-xs"
                  >
                    {(AI_PROVIDERS.find(p => p.id === row.provider)?.models || []).map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-mono uppercase text-slate-500 block mb-1">Fallback provider</label>
                  <select
                    value={row.fallback_provider || ''}
                    onChange={(e) => updateLocal(row.plan_id, { fallback_provider: e.target.value || null, fallback_model: e.target.value ? DEFAULT_MODEL_FOR[e.target.value] : null })}
                    className="w-full px-2 py-1.5 bg-slate-950 border border-slate-800 rounded text-white font-mono text-xs"
                  >
                    <option value="">— none —</option>
                    {AI_PROVIDERS.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-mono uppercase text-slate-500 block mb-1">Fallback model</label>
                  <select
                    value={row.fallback_model || ''}
                    onChange={(e) => updateLocal(row.plan_id, { fallback_model: e.target.value || null })}
                    disabled={!row.fallback_provider}
                    className="w-full px-2 py-1.5 bg-slate-950 border border-slate-800 rounded text-white font-mono text-xs disabled:opacity-40"
                  >
                    <option value="">—</option>
                    {(AI_PROVIDERS.find(p => p.id === row.fallback_provider)?.models || []).map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-mono uppercase text-slate-500 block mb-1">Cap USD/mo</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={row.monthly_cap_usd ?? ''}
                    onChange={(e) => updateLocal(row.plan_id, { monthly_cap_usd: e.target.value ? Number(e.target.value) : null })}
                    placeholder="—"
                    className="w-full px-2 py-1.5 bg-slate-950 border border-slate-800 rounded text-white font-mono text-xs placeholder:text-slate-600"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => savePlan(row)}
                  disabled={saving === row.plan_id}
                  className="px-3 py-1 rounded-md bg-cyan-500/15 hover:bg-cyan-500/25 border border-cyan-500/40 text-cyan-200 text-xs font-medium disabled:opacity-50"
                >
                  {saving === row.plan_id ? '…' : 'Save'}
                </button>
                <button
                  onClick={() => clearPlan(row.plan_id)}
                  disabled={saving === row.plan_id}
                  className="px-3 py-1 rounded-md bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-200 text-xs font-medium disabled:opacity-50"
                >
                  Clear
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// =====================================================================
// Cost Dashboard v2 — 30d cost, revenue, per-user profit margin.
// =====================================================================
const CostDashboardSection: React.FC = () => {
  const [totals, setTotals] = useState<any>(null);
  const [revenue, setRevenue] = useState<any>(null);
  const [summary, setSummary] = useState<any[]>([]);
  const [profit, setProfit] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [t, s, r, p] = await Promise.all([
          supabase.rpc('get_ai_usage_totals',    { range_days: 30 }),
          supabase.rpc('get_ai_usage_summary',   { range_days: 30 }),
          supabase.rpc('get_revenue_summary',    { range_days: 30 }),
          supabase.rpc('get_user_profit_summary',{ range_days: 30 }),
        ]);
        setTotals(t.data?.[0]);
        setSummary(s.data || []);
        setRevenue(r.data?.[0]);
        setProfit(p.data || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const marginPct = revenue?.gross_margin_pct != null ? Number(revenue.gross_margin_pct) : null;
  const marginUsd = revenue?.gross_margin_usd != null ? Number(revenue.gross_margin_usd) : 0;

  return (
    <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5 space-y-5">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-cyan-400 mb-2 flex items-center gap-2">
          <FiActivity className="text-cyan-400" />
          ▸ Cost + Revenue dashboard · last 30 days
        </div>
        <h3 className="text-lg font-semibold text-white">AI economics</h3>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 text-sm"><FiLoader className="animate-spin" /> Loading…</div>
      ) : (
        <>
          {/* ROI panel — the number that matters most for a $1B business. */}
          <div className="rounded-xl border border-cyan-500/30 bg-gradient-to-br from-cyan-500/[0.06] to-purple-500/[0.06] p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-cyan-300 mb-1">Top-up revenue</div>
                <div className="text-xl font-bold text-white font-mono">${Number(revenue?.topup_revenue || 0).toFixed(2)}</div>
                <div className="text-[10px] text-slate-500 font-mono">{Number(revenue?.topup_count || 0)} purchases</div>
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-purple-300 mb-1">AI cost</div>
                <div className="text-xl font-bold text-white font-mono">${Number(revenue?.ai_cost || 0).toFixed(2)}</div>
                <div className="text-[10px] text-slate-500 font-mono">{Number(revenue?.ai_call_count || 0)} calls</div>
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-1">Gross margin</div>
                <div className={`text-xl font-bold font-mono ${marginUsd >= 0 ? 'text-green-300' : 'text-red-300'}`}>
                  {marginUsd >= 0 ? '+' : ''}${marginUsd.toFixed(2)}
                </div>
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-1">Margin %</div>
                <div className={`text-xl font-bold font-mono ${(marginPct ?? 0) >= 50 ? 'text-green-300' : (marginPct ?? 0) >= 0 ? 'text-yellow-300' : 'text-red-300'}`}>
                  {marginPct != null ? `${marginPct.toFixed(1)}%` : '—'}
                </div>
              </div>
            </div>
          </div>

          {/* Usage KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <StatChip label="AI Spend" value={`$${Number(totals?.total_cost || 0).toFixed(2)}`} accent="cyan" />
            <StatChip label="Calls" value={Number(totals?.total_calls || 0).toLocaleString()} accent="purple" />
            <StatChip label="Tokens" value={Number(totals?.total_tokens || 0).toLocaleString()} accent="cyan" />
            <StatChip label="Users" value={Number(totals?.unique_users || 0).toLocaleString()} accent="purple" />
          </div>

          {/* Per-user profit table — combines topup revenue + AI cost per user */}
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">▸ Profit per user (top 25)</div>
            {profit.length === 0 ? (
              <div className="rounded-lg border border-slate-800 bg-slate-950/50 px-4 py-3 text-sm text-slate-500">
                No revenue or usage recorded yet in the last 30 days.
              </div>
            ) : (
              <div className="rounded-lg border border-slate-800 bg-slate-950/40 overflow-x-auto">
                <table className="w-full text-xs font-mono">
                  <thead>
                    <tr className="text-slate-500 text-left border-b border-slate-800">
                      <th className="px-3 py-2">User</th>
                      <th className="px-3 py-2 text-right">Top-up revenue</th>
                      <th className="px-3 py-2 text-right">AI cost</th>
                      <th className="px-3 py-2 text-right">Margin</th>
                      <th className="px-3 py-2 text-right">Calls</th>
                    </tr>
                  </thead>
                  <tbody>
                    {profit.slice(0, 25).map((r: any) => {
                      const m = Number(r.margin_usd);
                      return (
                        <tr key={r.user_id} className="border-b border-slate-800/60 last:border-0">
                          <td className="px-3 py-2 text-slate-300 truncate max-w-[220px]">{r.email || r.user_id?.slice(0, 8)}</td>
                          <td className="px-3 py-2 text-right text-cyan-300">${Number(r.topup_revenue).toFixed(2)}</td>
                          <td className="px-3 py-2 text-right text-purple-300">${Number(r.ai_cost).toFixed(3)}</td>
                          <td className={`px-3 py-2 text-right ${m >= 0 ? 'text-green-300' : 'text-red-300'}`}>
                            {m >= 0 ? '+' : ''}${m.toFixed(2)}
                          </td>
                          <td className="px-3 py-2 text-right text-slate-400">{Number(r.call_count).toLocaleString()}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Legacy top-spenders (kept for the "who's burning tokens" lens) */}
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">▸ Top AI spenders (all users, incl. non-topup)</div>
            {summary.length === 0 ? (
              <div className="rounded-lg border border-slate-800 bg-slate-950/50 px-4 py-3 text-sm text-slate-500">
                No usage recorded yet in the last 30 days.
              </div>
            ) : (
              <div className="rounded-lg border border-slate-800 bg-slate-950/40 overflow-x-auto">
                <table className="w-full text-xs font-mono">
                  <thead>
                    <tr className="text-slate-500 text-left border-b border-slate-800">
                      <th className="px-3 py-2">User</th>
                      <th className="px-3 py-2 text-right">Calls</th>
                      <th className="px-3 py-2 text-right">Tokens</th>
                      <th className="px-3 py-2 text-right">Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.slice(0, 10).map((r: any) => (
                      <tr key={r.user_id} className="border-b border-slate-800/60 last:border-0">
                        <td className="px-3 py-2 text-slate-300 truncate max-w-[220px]">{r.email || r.user_id?.slice(0, 8)}</td>
                        <td className="px-3 py-2 text-right text-slate-400">{Number(r.calls).toLocaleString()}</td>
                        <td className="px-3 py-2 text-right text-slate-400">{Number(r.total_tokens).toLocaleString()}</td>
                        <td className="px-3 py-2 text-right text-cyan-300">${Number(r.total_cost).toFixed(3)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const StatChip: React.FC<{ label: string; value: string; accent: 'cyan' | 'purple' }> = ({ label, value, accent }) => (
  <div className={`rounded-lg border p-3 ${accent === 'cyan' ? 'border-cyan-500/25 bg-cyan-500/[0.05]' : 'border-purple-500/25 bg-purple-500/[0.05]'}`}>
    <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
    <div className={`text-lg font-bold ${accent === 'cyan' ? 'text-cyan-200' : 'text-purple-200'}`}>{value}</div>
  </div>
);

export default AISettingsPanel;
